import React, { useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, IconButton, MenuItem,
  Select, Typography, InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const GRADE_OPTIONS = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8",
];

/* shared field style — border-only, no label above */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: "13px",
    "& fieldset":           { borderColor: "#D5D7DA" },
    "&:hover fieldset":     { borderColor: "#4A7BA8" },
    "&.Mui-focused fieldset":{ borderColor: "#244C77" },
  },
  "& .MuiInputBase-input::placeholder": { color: "#9CA3AF", opacity: 1 },
};

const WithdrawForm = ({ handleClose }) => {
  const fileInputRef = useRef(null);

  const [formValues, setFormValues] = useState({
    studentName:  "",
    registrationNo: "",
    grade:        "",
    parentName:   "",
    phoneNumber:  "",
    requestDate:  new Date().toISOString().split("T")[0],
    reason:       "",
    email:        "",
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const [errors,       setErrors]       = useState({});
  const [loading,      setLoading]      = useState(false);
  const [toast,        setToast]        = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const required = ["studentName", "registrationNo", "grade", "parentName", "phoneNumber", "reason"];
    const newErrors = {};
    required.forEach((f) => { if (!formValues[f]) newErrors[f] = "Required"; });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* file handling */
  const handleFileSelect = (file) => {
    if (file) setUploadedFile(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    /* Try multiple session keys for the student's numeric ID */
    const rawId =
      sessionStorage.getItem("userId") ||
      sessionStorage.getItem("studentId") ||
      sessionStorage.getItem("applicationId") ||
      "0";
    const studentId = Number(rawId);

    if (!studentId || isNaN(studentId)) {
      setToast({ open: true, severity: "error", message: "Session expired. Please log in again." });
      return;
    }

    setLoading(true);
    try {
      /* Backend accepts { studentId (number), reason (string) } */
      const payload = { studentId, reason: formValues.reason };

      /* postData throws on any non-2xx — so ANY return value here = success */
      await postData("student/withdrawl-enquirie/create", payload);
      setToast({ open: true, severity: "success", message: "Withdrawal enquiry submitted successfully!" });
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      /* Extract the actual server-side error message when available */
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to submit enquiry. Please try again.";
      setToast({ open: true, severity: "error", message: serverMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}
      sx={{ width: 520, borderRadius: "20px", p: "28px 32px", position: "relative", bgcolor: "#fff" }}>

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      {/* ── Close button ── */}
      <IconButton onClick={handleClose} size="small"
        sx={{ position: "absolute", top: 16, right: 16, color: "#6B7280" }}>
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: "10px", border: "1px solid #E5E7EB",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <LibraryBooksIcon sx={{ color: "#244C77", fontSize: "22px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "17px", color: "#111827", lineHeight: 1.3 }}>
            Withdrawal Enquires
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#8F95B2", mt: 0.2 }}>
            Withdrawal list and their status
          </Typography>
        </Box>
      </Box>

      {/* ── Fields ── */}
      <Grid container spacing={2}>

        {/* Row 1 */}
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="studentName" placeholder="Student Name"
            value={formValues.studentName} onChange={handleChange}
            error={!!errors.studentName} sx={fieldSx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="registrationNo" placeholder="Reg.No"
            value={formValues.registrationNo} onChange={handleChange}
            error={!!errors.registrationNo} sx={fieldSx} />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={6}>
          <Select fullWidth size="small" name="grade" value={formValues.grade}
            onChange={handleChange} displayEmpty error={!!errors.grade}
            sx={{ borderRadius: "10px", fontSize: "13px", color: formValues.grade ? "#111827" : "#9CA3AF",
              "& fieldset": { borderColor: "#D5D7DA" },
              "&:hover fieldset": { borderColor: "#4A7BA8" },
              "&.Mui-focused fieldset": { borderColor: "#244C77" },
            }}>
            <MenuItem value="" disabled sx={{ color: "#9CA3AF", fontSize: "13px" }}>Grade</MenuItem>
            {GRADE_OPTIONS.map((g) => <MenuItem key={g} value={g} sx={{ fontSize: "13px" }}>{g}</MenuItem>)}
          </Select>
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="parentName" placeholder="Parent Name"
            value={formValues.parentName} onChange={handleChange}
            error={!!errors.parentName} sx={fieldSx} />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="phoneNumber" placeholder="Parent phone No"
            value={formValues.phoneNumber} onChange={handleChange}
            error={!!errors.phoneNumber} sx={fieldSx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="requestDate" type="date"
            value={formValues.requestDate} onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarTodayOutlinedIcon sx={{ fontSize: "16px", color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              ...fieldSx,
              "& input[type='date']::-webkit-calendar-picker-indicator": { display: "none" },
            }}
          />
        </Grid>

        {/* Row 4 */}
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="reason" placeholder="Reason for Withdrawal"
            value={formValues.reason} onChange={handleChange}
            error={!!errors.reason} sx={fieldSx} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" name="email" type="email"
            placeholder="Email Id of the Parent"
            value={formValues.email} onChange={handleChange}
            error={!!errors.email} sx={fieldSx} />
        </Grid>

        {/* Row 5 — File upload */}
        <Grid item xs={12}>
          <Box
            onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `1.5px dashed ${dragOver ? "#244C77" : "#D5D7DA"}`,
              borderRadius: "10px",
              backgroundColor: dragOver ? "#f0edff" : "#FAFAFA",
              py: 2.5, textAlign: "center", cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#4A7BA8", backgroundColor: "#f7f5ff" },
            }}
          >
            <input ref={fileInputRef} type="file" accept=".svg,.png,.jpg,.gif,.pdf,.doc,.docx"
              style={{ display: "none" }} onChange={(e) => handleFileSelect(e.target.files?.[0])} />

            <CloudUploadOutlinedIcon sx={{ fontSize: "32px", color: "#9CA3AF", mb: 0.5 }} />

            {uploadedFile ? (
              <Typography sx={{ fontSize: "13px", color: "#244C77", fontWeight: 500 }}>
                {uploadedFile.name}
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: "13px", color: "#374151" }}>
                  <span style={{ color: "#244C77", fontWeight: 600, cursor: "pointer" }}>
                    Click to upload
                  </span>
                  {" "}or drag and drop
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#9CA3AF", mt: 0.3 }}>
                  SVG, PNG, JPG or GIF (max. 800×400px)
                </Typography>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* ── Buttons ── */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={handleClose} disabled={loading}
          sx={{
            minWidth: 140, fontSize: "14px", textTransform: "none",
            borderRadius: "10px", border: "1.5px solid #4caf50", color: "#4caf50",
            "&:hover": { backgroundColor: "#f1fdf4", borderColor: "#388e3c" },
          }}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}
          sx={{
            minWidth: 140, fontSize: "14px", textTransform: "none",
            borderRadius: "10px", backgroundColor: "#2D3A5C", color: "#fff",
            "&:hover": { backgroundColor: "#1e2a45" },
          }}>
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
};

export default WithdrawForm;
